import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Heart, ShieldCheck, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    title: 'Request Help',
    subtitle: 'SVdP Mary, Mother of God Parish',
    tagline: 'Journeying with our neighbors in faith and service',
    serving: 'Serving the Uptown & Edgewater communities of Chicago',
    emergency: 'If this is an emergency, please call 911 or 311 for immediate assistance.',
    personalInfo: 'Personal Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    email: 'Email',
    address: 'Street Address',
    city: 'City',
    state: 'State',
    zip: 'ZIP Code',
    assistanceNeeded: 'Assistance Needed',
    selectTypes: 'Select the type(s) of assistance you need',
    rent: 'Rent',
    utilities: 'Utilities',
    medication: 'Medication',
    ids: 'IDs / Documents',
    transportation: 'Transportation',
    food: 'Food',
    clothing: 'Clothing',
    other: 'Other',
    amountRequested: 'Amount Requested ($)',
    details: 'Details (Optional)',
    detailsPlaceholder: 'Please provide any additional details about your request...',
    submit: 'Submit Request',
    submitting: 'Submitting...',
    thankYou: 'Thank you!',
    successMessage: 'Your request has been submitted successfully. Case Number: ',
    volunteerContact: 'A volunteer will contact you within 48 hours.',
    godBless: 'God bless you,',
    parish: 'SVdP Mary Mother of God',
    submitAnother: 'Submit Another Request',
    required: 'This field is required'
  },
  es: {
    title: 'Solicitar Ayuda',
    subtitle: 'SVdP Maria, Madre de Dios',
    tagline: 'Caminando junto a nuestros vecinos en fe y servicio',
    serving: 'Sirviendo a las comunidades de Uptown y Edgewater en Chicago',
    emergency: 'Si esto es una emergencia, llame al 911 o al 311 para asistencia inmediata.',
    personalInfo: 'Informacion Personal',
    firstName: 'Nombre',
    lastName: 'Apellido',
    phone: 'Numero de Telefono',
    email: 'Correo Electronico',
    address: 'Direccion',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Codigo Postal',
    assistanceNeeded: 'Asistencia Necesaria',
    selectTypes: 'Seleccione el tipo de asistencia que necesita',
    rent: 'Alquiler',
    utilities: 'Servicios Publicos',
    medication: 'Medicamentos',
    ids: 'Identificaciones / Documentos',
    transportation: 'Transporte',
    food: 'Comida',
    clothing: 'Ropa',
    other: 'Otro',
    amountRequested: 'Cantidad Solicitada ($)',
    details: 'Detalles (Opcional)',
    detailsPlaceholder: 'Por favor proporcione detalles adicionales sobre su solicitud...',
    submit: 'Enviar Solicitud',
    submitting: 'Enviando...',
    thankYou: 'Gracias!',
    successMessage: 'Su solicitud ha sido enviada exitosamente. Numero de Caso: ',
    volunteerContact: 'Un voluntario se comunicara con usted dentro de 48 horas.',
    godBless: 'Que Dios te bendiga,',
    parish: 'SVdP Maria Madre de Dios',
    submitAnother: 'Enviar Otra Solicitud',
    required: 'Este campo es obligatorio'
  }
};

const PublicIntakeForm = () => {
  const [lang, setLang] = useState('en');
  const [submitted, setSubmitted] = useState(false);
  const [caseNumber, setCaseNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [boundaryCheck, setBoundaryCheck] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: 'IL',
    zip_code: '',
    request_types: [],
    amount_requested: '',
    details: '',
    entry_type: 'self_service'
  });

  const t = translations[lang];

  const reassurance = {
    en: {
      welcome: "We're glad you reached out. Let's take this one step at a time.",
      confidential: "We're asking these questions to get a better picture of how we might journey with you. A volunteer will reach out to review everything together.",
      noDocs: "You don't need to have all documents ready right now — a volunteer will help you through the process.",
      noPromises: "Submitting this form does not guarantee assistance, but your request will be reviewed carefully by our team.",
      followUp: "A volunteer will contact you within 48 hours. There is no cost for this service."
    },
    es: {
      welcome: "Nos alegra que se haya comunicado. Vamos paso a paso.",
      confidential: "Hacemos estas preguntas para entender mejor como podemos acompanarle en este camino. Un voluntario se comunicara con usted para revisar todo juntos.",
      noDocs: "No necesita tener todos los documentos listos ahora — un voluntario le ayudara durante el proceso.",
      noPromises: "Enviar este formulario no garantiza asistencia, pero su solicitud sera revisada cuidadosamente por nuestro equipo.",
      followUp: "Un voluntario se comunicara con usted dentro de 48 horas. No hay costo por este servicio."
    }
  };
  const r = reassurance[lang];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (type) => {
    setFormData(prev => ({
      ...prev,
      request_types: prev.request_types.includes(type)
        ? prev.request_types.filter(t => t !== type)
        : [...prev.request_types, type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.phone || 
        !formData.email || !formData.address || !formData.city || !formData.zip_code || 
        formData.request_types.length === 0) {
      toast.error(t.required);
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/intake/submit`, {
        ...formData,
        amount_requested: formData.amount_requested ? parseFloat(formData.amount_requested) : null,
        recaptcha_token: 'test-token'
      });
      
      if (response.data.success === false && response.data.boundary_check === 'Outside_State') {
        // Out of state — rejected
        setBoundaryCheck('Outside_State');
        setRejectionMessage(response.data.message);
        setSubmitted(true);
      } else {
        setCaseNumber(response.data.case_number);
        setBoundaryCheck(response.data.boundary_check || 'Inside');
        setSubmitted(true);
        toast.success(t.successMessage + response.data.case_number);
      }
    } catch (error) {
      console.error('Submission error:', error);
      // Handle validation errors properly
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Submission failed';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: 'IL',
      zip_code: '',
      request_types: [],
      amount_requested: '',
      details: '',
      entry_type: 'self_service'
    });
    setSubmitted(false);
    setCaseNumber('');
    setBoundaryCheck(null);
    setRejectionMessage('');
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none";

  if (submitted) {
    // Out of state rejection
    if (boundaryCheck === 'Outside_State') {
      return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
          <div className="container mx-auto px-4 py-12 max-w-2xl">
            {/* Language Toggle */}
            <div className="flex justify-end mb-6">
              <div className="inline-flex rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-1" data-testid="language-toggle">
                <button
                  onClick={() => setLang('en')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    lang === 'en' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="lang-en-btn"
                >
                  English
                </button>
                <button
                  onClick={() => setLang('es')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    lang === 'es' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="lang-es-btn"
                >
                  Espanol
                </button>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-500/20 border border-amber-500/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                  </svg>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2" data-testid="outside-state-title">
                {lang === 'es' ? 'Fuera de Nuestra Area' : 'Outside Our Service Area'}
              </h1>
            </div>

            <div className="glass-panel p-8" data-testid="outside-state-message">
              <p className="text-slate-300 text-center mb-6">
                {lang === 'es'
                  ? 'Agradecemos que se haya comunicado con nosotros. Lamentablemente, nuestra conferencia parroquial solo puede brindar asistencia directa a vecinos dentro de nuestra area local en Chicago, Illinois. Le animamos a comunicarse con su consejo local de San Vicente de Paul o visitar www.svdpusa.net para encontrar una conferencia cerca de usted que pueda ayudarle.'
                  : rejectionMessage || 'We appreciate you reaching out. Unfortunately, our parish conference is only able to provide direct assistance to neighbors within our local area in Chicago, Illinois. We encourage you to contact your local St. Vincent de Paul council or visit www.svdpusa.net to find a conference near you that may be able to help.'
                }
              </p>

              <div className="text-center mb-6">
                <a
                  href="https://www.svdpusa.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#0072BC] hover:text-[#0072BC]/80 underline"
                  data-testid="svdp-national-link"
                >
                  {lang === 'es' ? 'Visitar SVdP Nacional' : 'Visit SVdP National Website'}
                </a>
              </div>

              <div className="text-center">
                <p className="text-slate-400 mb-1">{t.godBless}</p>
                <p className="font-semibold text-white">{t.parish}</p>
              </div>

              <div className="mt-8 text-center">
                <button onClick={resetForm} className="btn-primary" data-testid="submit-another-btn">
                  {t.submitAnother}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Outside local (in Illinois but not in parish ZIP) — referral
    if (boundaryCheck === 'Outside_Local') {
      return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
          <div className="container mx-auto px-4 py-12 max-w-2xl">
            {/* Language Toggle */}
            <div className="flex justify-end mb-6">
              <div className="inline-flex rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-1" data-testid="language-toggle">
                <button
                  onClick={() => setLang('en')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    lang === 'en' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="lang-en-btn"
                >
                  English
                </button>
                <button
                  onClick={() => setLang('es')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    lang === 'es' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="lang-es-btn"
                >
                  Espanol
                </button>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#0072BC]/20 border border-[#0072BC]/30 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#0072BC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                  </svg>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2" data-testid="outside-local-title">{t.thankYou}</h1>
            </div>

            <div className="glass-panel p-8" data-testid="outside-local-message">
              <div className="text-center mb-6">
                <div className="inline-block bg-[#0072BC]/15 border border-[#0072BC]/30 px-6 py-3 rounded-xl mb-4">
                  <p className="text-sm text-slate-400 mb-1">{lang === 'es' ? 'Numero de Caso' : 'Case Number'}</p>
                  <p className="text-2xl font-bold text-[#0072BC]" data-testid="case-number">{caseNumber}</p>
                </div>
              </div>

              <p className="text-slate-300 text-center mb-6">
                {lang === 'es'
                  ? 'Gracias por comunicarse con nosotros. Segun la informacion que nos proporciono, parece que se encuentra fuera de los limites inmediatos de nuestra parroquia. Aunque nuestra conferencia atiende a vecinos dentro de nuestra area local, nos gustaria ayudarle a conectarse con una conferencia de San Vicente de Paul mas cercana a usted. Un voluntario se comunicara para compartir opciones y referencias.'
                  : 'Thank you for reaching out to us. Based on the information you provided, it appears you may be outside our immediate parish boundaries. While our conference serves neighbors within our local area, we would like to help connect you with a St. Vincent de Paul conference closer to you. A volunteer will be in touch to discuss options and share any referrals we can offer.'
                }
              </p>

              <div className="text-center">
                <p className="text-slate-400 mb-1">{t.godBless}</p>
                <p className="font-semibold text-white">{t.parish}</p>
              </div>

              <div className="mt-8 text-center">
                <button onClick={resetForm} className="btn-primary" data-testid="submit-another-btn">
                  {t.submitAnother}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Inside boundaries — normal success
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="container mx-auto px-4 py-12 max-w-2xl">
          {/* Language Toggle */}
          <div className="flex justify-end mb-6">
            <div className="inline-flex rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-1" data-testid="language-toggle">
              <button
                onClick={() => setLang('en')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  lang === 'en' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                }`}
                data-testid="lang-en-btn"
              >
                English
              </button>
              <button
                onClick={() => setLang('es')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  lang === 'es' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                }`}
                data-testid="lang-es-btn"
              >
                Espanol
              </button>
            </div>
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{t.thankYou}</h1>
          </div>

          <div className="glass-panel p-8" data-testid="success-message">
            <div className="text-center mb-6">
              <div className="inline-block bg-[#0072BC]/15 border border-[#0072BC]/30 px-6 py-3 rounded-xl mb-4">
                <p className="text-sm text-slate-400 mb-1">Case Number</p>
                <p className="text-2xl font-bold text-[#0072BC]" data-testid="case-number">{caseNumber}</p>
              </div>
            </div>

            <div className="space-y-4 text-slate-300 mb-6">
              <p className="text-center">{t.volunteerContact}</p>
              <div className="emergency-banner">
                <p className="text-sm">{t.emergency}</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-slate-400 mb-1">{t.godBless}</p>
              <p className="font-semibold text-white">{t.parish}</p>
            </div>

            <div className="mt-8 text-center">
              <button onClick={resetForm} className="btn-primary" data-testid="submit-another-btn">
                {t.submitAnother}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Language Toggle */}
        <div className="flex justify-end mb-6">
          <div className="inline-flex rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-1" data-testid="language-toggle">
            <button
              onClick={() => setLang('en')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                lang === 'en' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid="lang-en-btn"
            >
              English
            </button>
            <button
              onClick={() => setLang('es')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                lang === 'es' ? 'bg-[#0072BC] text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid="lang-es-btn"
            >
              Espanol
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{t.title}</h1>
          <p className="text-lg text-slate-400">{t.subtitle}</p>
          <p className="text-base text-slate-400 italic mt-1">{t.tagline}</p>
          <p className="text-sm text-slate-500">{t.serving}</p>
        </div>

        {/* Emergency Notice */}
        <div className="emergency-banner mb-8" data-testid="emergency-notice">
          <p>{t.emergency}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-panel p-8" data-testid="intake-form">
          {/* Welcome Reassurance */}
          <div className="mb-8 px-4 py-3 bg-[#0072BC]/10 border border-[#0072BC]/20 rounded-xl flex items-start gap-3" data-testid="neighbor-welcome-msg">
            <Heart className="w-5 h-5 text-[#0072BC] mt-0.5 shrink-0" />
            <p className="text-sm text-slate-300">{r.welcome}</p>
          </div>

          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-[#0072BC]/30">
              {t.personalInfo}
            </h2>
            {/* Confidentiality Note */}
            <div className="mb-5 px-3 py-2 bg-white/5 border border-white/5 rounded-lg flex items-start gap-2" data-testid="neighbor-confidential-msg">
              <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-400">{r.confidential}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.firstName} *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="first-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.lastName} *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="last-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.phone} *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.email} *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.address} *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="address-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.city} *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="city-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.state} *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="state-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.zip} *</label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                  data-testid="zip-input"
                />
              </div>
            </div>
          </div>

          {/* Assistance Needed */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-[#0072BC]/30">
              {t.assistanceNeeded}
            </h2>
            {/* No docs needed reassurance */}
            <div className="mb-4 px-3 py-2 bg-white/5 border border-white/5 rounded-lg flex items-start gap-2" data-testid="neighbor-nodocs-msg">
              <FileText className="w-4 h-4 text-[#0072BC] mt-0.5 shrink-0" />
              <p className="text-xs text-slate-400">{r.noDocs}</p>
            </div>
            
            <p className="text-slate-400 mb-4">{t.selectTypes}</p>

            <div className="grid md:grid-cols-2 gap-4" data-testid="assistance-types">
              {['rent', 'utilities', 'medication', 'ids', 'transportation', 'food', 'clothing', 'other'].map(type => (
                <label key={type} className="flex items-center space-x-3 p-3 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.request_types.includes(type)}
                    onChange={() => handleCheckboxChange(type)}
                    className="w-5 h-5 rounded bg-white/5 border-white/20 text-[#0072BC] focus:ring-[#0072BC] accent-[#0072BC]"
                    data-testid={`checkbox-${type}`}
                  />
                  <span className="font-medium text-slate-300">{t[type]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Amount Requested */}
          {(formData.request_types.includes('rent') || formData.request_types.includes('utilities')) && (
            <div className="mb-8">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.amountRequested}</label>
              <input
                type="number"
                name="amount_requested"
                value={formData.amount_requested}
                onChange={handleInputChange}
                className={inputClass}
                min="0"
                step="0.01"
                data-testid="amount-input"
              />
            </div>
          )}

          {/* Details */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-300 mb-1.5">{t.details}</label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleInputChange}
              className={`${inputClass} resize-none min-h-[120px]`}
              placeholder={t.detailsPlaceholder}
              data-testid="details-textarea"
            />
          </div>

          {/* Pre-Submit Reassurance */}
          <div className="mb-6 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl space-y-2" data-testid="neighbor-presubmit-msg">
            <p className="text-sm text-slate-300 flex items-start gap-2">
              <Heart className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>{r.followUp}</span>
            </p>
            <p className="text-xs text-slate-400 ml-6">{r.noPromises}</p>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center gap-2 px-8 py-3 text-lg"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.submitting}
                </>
              ) : (
                t.submit
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublicIntakeForm;
